import { useEffect, useRef } from 'react'

import awsconfig from '../aws-exports'
import { STAGE, STAGES } from '../helpers/stage'

// Scalar kommt vom CDN statt als Dependency: die Doku ist eine oeffentliche
// Seite und soll das Dashboard-Bundle nicht vergroessern, das jeder
// eingeloggte Nutzer laedt.
//
// Exakte Version plus SRI-Hash, damit ein Major-Sprung im CDN die oeffentliche
// Doku nicht ohne Commit veraendert. Referenziert wird der Originalpfad
// (…/dist/browser/standalone.js) und nicht die Kurzform — die minifiziert
// jsDelivr dynamisch, und dynamisch erzeugte Dateien vertragen kein SRI.
//
// Update-Prozedur: Version hochziehen, Hash neu bilden, beides zusammen
// committen:
//   curl -s <url> | openssl dgst -sha384 -binary | openssl base64 -A
const SCALAR_SRC =
  'https://cdn.jsdelivr.net/npm/@scalar/api-reference@1.70.0/dist/browser/standalone.js'
const SCALAR_SRI =
  'sha384-C7UjpUKXe2xpcKhU1f0d6mVriqbtN0f9QsgEdE8Q5cSQdY0Z+u/mCdhfxX6v/Cl/'

// Die Spec liegt bewusst als .json und nicht als .yaml in public/: Amplify
// schreibt per SPA-Catch-all praktisch jeden Pfad auf die React-index.html um
// und laesst nur eine Endungs-Whitelist durch. `json` steht darauf, `yaml`
// nicht — als .yaml waere die Datei im Build vorhanden, aber nicht abrufbar.
const SPEC_URL = '/api-docs/openapi.json'

// Die Spec nennt nur production (restify.aivy.app), weil Kunden nur dort
// testen. Auf develop und staging haengt die Seite zusaetzlich das API Gateway
// dieser Umgebung davor und waehlt es vor — so laesst sich ein Endpoint mit
// Token aus derselben Umgebung ausprobieren, bevor er in production ist. Die
// Stage kommt aus aws-exports (siehe helpers/stage), nicht aus dem Login: die
// Seite ist oeffentlich und rendert vor jeder Auth-Pruefung.
const stageServer = () => {
  if (STAGE === STAGES.PRODUCTION) return undefined

  const restify = awsconfig?.aws_cloud_logic_custom?.find(
    ({ name }) => name === 'restify'
  )
  if (!restify?.endpoint) return undefined

  return { url: `${restify.endpoint}/ats/1`, description: `${STAGE} (intern)` }
}

const loadSpec = async () => {
  const server = stageServer()
  if (!server) return { url: SPEC_URL }

  const spec = await fetch(SPEC_URL).then((res) => res.json())
  return { content: { ...spec, servers: [server, ...(spec.servers || [])] } }
}

const CUSTOM_CSS = `
  :root {
    --scalar-font: 'Fira Sans', ui-sans-serif, system-ui, sans-serif;
    --scalar-font-code: 'Fira Code', ui-monospace, SFMono-Regular, monospace;
  }
  .light-mode {
    --scalar-color-accent: #4264dc;
    --scalar-background-accent: #ebeffd;
  }
  .dark-mode {
    --scalar-color-accent: #4d7bf3;
    --scalar-background-accent: rgba(66, 100, 220, 0.2);
  }
`

async function mountScalar(element) {
  if (!window.Scalar || !element) return

  const source = await loadSpec()

  window.Scalar.createApiReference(element, {
    ...source,

    // Bewusst KEIN proxyUrl: restify.aivy.app schickt auf jedem /ats/*-Pfad die
    // CORS-Header mit (OPTIONS-Mock-Integration im API Gateway), der Browser
    // darf also direkt ran. Mit proxyUrl liefe jeder Try-it-Request inklusive
    // Kunden-Token ueber einen fremden Server.

    // showDeveloperTools bleibt auf dem Default 'localhost': die Leiste mit
    // "Configure / Share / Deploy" ist nur beim lokalen Entwickeln sichtbar.

    defaultHttpClient: { targetKey: 'shell', clientKey: 'curl' },

    // Scalar sendet standardmaessig Telemetrie und blendet einen "Ask AI"-Knopf
    // ein, der Fragen an einen Dritten schickt. Auf einer oeffentlichen
    // Kundendoku beides aus.
    telemetry: false,
    agent: { disabled: true },

    metaData: {
      title: 'Aivy API Dokumentation',
      ogTitle: 'Aivy API Dokumentation',
      ogDescription:
        'Interaktive Dokumentation der Aivy Partner API — inklusive Beispiel-Responses und Try-it direkt im Browser.'
    },

    // Nur Akzentfarbe und Schrift werden ueberschrieben. Alles Weitere bleibt
    // Scalars Default, damit ein Update der Library nicht halbe Themes mischt.
    customCss: CUSTOM_CSS
  })
}

export default function ApiDocs() {
  const containerRef = useRef(null)

  useEffect(() => {
    document.title = 'Aivy API Dokumentation'

    const existing = document.querySelector(`script[src="${SCALAR_SRC}"]`)

    // Beim Zurueckkehren auf die Route liegt das Script schon im DOM, ein
    // zweites <script> wuerde nicht erneut laden und onload nie feuern.
    if (existing) {
      mountScalar(containerRef.current)
      return undefined
    }

    const script = document.createElement('script')
    script.src = SCALAR_SRC
    script.integrity = SCALAR_SRI
    script.crossOrigin = 'anonymous'
    script.async = true
    script.onload = () => mountScalar(containerRef.current)
    document.body.appendChild(script)

    return undefined
  }, [])

  return <div ref={containerRef} className='min-h-screen' />
}
